import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Dialog, Button, Input } from '@/design-system';
import { useAuth } from '@/core/auth';
import { authService } from '@/features/auth';
import { isStrongPassword } from './passwordPolicy';
import { PasswordRequirements } from './PasswordRequirements';

const isStrong = isStrongPassword;

/**
 * Modal bloqueante do manager: exibido quando o backend marca `mustResetPassword`
 * (senha fraca detectada no login). Só sai trocando por uma senha forte ou saindo.
 */
export function ForcePasswordResetModal() {
  const { user, refreshUser, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!user?.mustResetPassword) return null;

  const canSubmit = current.length > 0 && isStrong(next) && next === confirm && !saving;

  const handleSubmit = async () => {
    setError(null);
    if (!isStrong(next)) return setError('A nova senha não atende aos requisitos.');
    if (next !== confirm) return setError('A confirmação não corresponde.');
    if (next === current) return setError('A nova senha deve ser diferente da atual.');
    setSaving(true);
    try {
      await authService.changePassword(current, next);
      await refreshUser();
    } catch (err: unknown) {
      const m = isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : err instanceof Error
          ? err.message
          : undefined;
      setError(m || 'Não foi possível alterar a senha.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog isOpen onClose={() => {}} title="Atualização de senha obrigatória" size="md" footer={(
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={logout} disabled={saving}>
          Sair
        </Button>
        <Button variant="primary" onClick={handleSubmit} isLoading={saving} disabled={!canSubmit}>
          Salvar nova senha
        </Button>
      </div>
    )}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          Sua senha atual não atende à nova política de segurança. Defina uma nova senha para continuar.
        </p>
        <Input
          label="Senha atual"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="Nova senha"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <PasswordRequirements value={next} />
        <Input
          label="Confirmar nova senha"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm.length > 0 && confirm !== next ? 'As senhas não coincidem.' : undefined}
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Dialog>
  );
}
